import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Check, ArrowRight, ExternalLink } from "lucide-react";
import { useSite } from "@/hooks/useSite";
import { LoadState } from "@/components/ErrorBoundary";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/site/Footer";
import { api, friendlyError } from "@/lib/api";
import TurnstileWidget from "@/components/TurnstileWidget";

const steps = [
  ["01", "ANKOMMEN", "Im Studio wird kurz besprochen, welche Bilder entstehen sollen und worauf beim Tattoo besonders geachtet wird."],
  ["02", "DU + DEIN TATTOO", "Natürliche Aufnahmen beim Sitzen, Gehen oder mit Handy und Tablet. Keine steifen Posen – Bilder, die nach dir aussehen."],
  ["03", "TATTOO DETAILS", "Danach steht das Tattoo selbst im Fokus: Licht, Perspektive, Körperform, Linien, Flächen und Kontraste."],
  ["04", "DEINE FOTOS", "Nach Auswahl und Bearbeitung bekommst du deine Bilder über einen persönlichen Download-Link."]
];

export default function Fotografie() {
  const { site, status, error, reload } = useSite();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", tattoo: "", placement: "", preferred_date: "",
    message: "", privacy_consent: false, image_consent: false, honeypot: ""
  });
  const [state, setState] = useState({ sending: false, ok: false, error: "" });
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileReset, setTurnstileReset] = useState(0);
  const invitationCode = new URLSearchParams(window.location.search).get("einladung") || "";
  const [invitation, setInvitation] = useState({ checking: Boolean(invitationCode), valid: false, name: "" });

  useEffect(() => {
    if (!invitationCode) return;
    api.get(`/photography-invitations/${encodeURIComponent(invitationCode)}`)
      .then((r) => setInvitation({ checking: false, valid: true, name: r.data.name || "" }))
      .catch(() => setInvitation({ checking: false, valid: false, name: "" }));
  }, [invitationCode]);

  if (!site) return <LoadState status={status} error={error} onRetry={reload} label="Wird geladen" />;

  const change = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((x) => ({ ...x, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (invitationCode && !invitation.valid) {
      setState({ sending: false, ok: false, error: invitation.checking ? "Die Einladung wird noch geprüft." : "Diese Einladung ist ungültig oder wurde bereits verwendet." });
      return;
    }
    if (!form.privacy_consent) {
      setState({ sending: false, ok: false, error: "Bitte bestätige zuerst die Datenschutzerklärung." });
      return;
    }
    setState({ sending: true, ok: false, error: "" });
    try {
      const body = new FormData();
      Object.entries(form).forEach(([key, value]) => body.append(key, String(value)));
      body.append("turnstile_token", turnstileToken);
      if (invitationCode) body.append("invitation_code", invitationCode);
      await api.post("/photography-inquiries", body);
      setState({ sending: false, ok: true, error: "" });
    } catch (err) {
        setTurnstileToken("");
        setTurnstileReset((x) => x + 1);
      setState({ sending: false, ok: false, error: friendlyError(err) });
    }
  };

  return (
    <>
      <Seo
        title="Tattoo Fotografie – ARTPART × Frau Vielfalt"
        description="Professionelle Tattoo-Fotografie für ARTPART Kund:innen: natürliche Körperaufnahmen, Tattoo-Details und bearbeitete Bilder als Download."
        path="/fotografie"
      />
      <main className="bg-paper text-ink">
        <header className="border-b border-paper-line bg-paper">
          <div className="ap-container flex h-[72px] items-center justify-between">
            <Link to="/" className="font-display text-xl font-black uppercase tracking-[0.14em]">
              Art<span className="text-signal">Part</span>
            </Link>
            <Link to="/" className="ap-stamp transition-colors hover:text-signal">Zurück zu ArtPart</Link>
          </div>
        </header>

        <section className="relative overflow-hidden border-b border-paper-line bg-ink py-20 sm:py-28">
          <div className="absolute right-[-8%] top-[-30%] h-[520px] w-[520px] rounded-full border border-white/10" />
          <div className="absolute right-[8%] top-[12%] h-[260px] w-[260px] rounded-full border border-signal/40" />
          <div className="ap-container relative max-w-5xl">
            <p className="ap-stamp text-signal">ARTPART × FRAU VIELFALT · FOTOGRAFIE</p>
            <h1 className="mt-6 max-w-4xl font-display text-5xl font-black uppercase leading-[0.94] tracking-tight text-paper sm:text-7xl">
              Dein Tattoo.<br />Richtig festgehalten.
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-paper/75 sm:text-xl">
              Professionelle Bilder, die nicht nur dein Tattoo dokumentieren, sondern zeigen, wie es mit deinem Körper lebt.
            </p>
            <a href="#anfrage" className="ap-btn-invert mt-9">Fotoshooting anfragen <ArrowRight size={15} /></a>
          </div>
        </section>

        <section className="border-b border-paper-line py-16 sm:py-24">
          <div className="ap-container grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <p className="ap-stamp">NICHT EINFACH EIN TATTOO-FOTO</p>
              <h2 className="ap-h2 mt-4">Zwei Bildwelten. Ein Projekt.</h2>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <article className="border border-paper-line bg-paper-surface p-7">
                <p className="ap-stamp text-signal">01 · MENSCH</p>
                <h3 className="mt-4 font-display text-2xl font-black uppercase">Du + dein Tattoo</h3>
                <p className="ap-body mt-4 text-charcoal">Sitzen, gehen, bewegen, Handy oder Tablet in der Hand. Das Tattoo wird Teil einer natürlichen Situation statt nur isoliert gezeigt.</p>
              </article>
              <article className="border border-paper-line bg-paper-surface p-7">
                <p className="ap-stamp text-signal">02 · DETAIL</p>
                <h3 className="mt-4 font-display text-2xl font-black uppercase">Das Tattoo</h3>
                <p className="ap-body mt-4 text-charcoal">Gezielte Detailaufnahmen mit Fokus auf Licht, Perspektive, Körperform, Linien, Flächen und Kontraste.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="border-b border-paper-line bg-paper-surface py-16 sm:py-24">
          <div className="ap-container">
            <p className="ap-stamp">DER ABLAUF</p>
            <h2 className="ap-h2 mt-4">Vom Studio bis zum Download.</h2>
            <div className="mt-10 grid gap-px bg-paper-line md:grid-cols-2">
              {steps.map(([n, title, body]) => (
                <article key={n} className="bg-paper p-7 sm:p-9">
                  <div className="font-display text-4xl font-black text-signal">{n}</div>
                  <h3 className="mt-5 font-display text-xl font-black uppercase tracking-wide">{title}</h3>
                  <p className="ap-body mt-3 text-charcoal">{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-paper-line py-16 sm:py-24">
          <div className="ap-container grid gap-10 lg:grid-cols-[1fr_0.8fr] lg:items-center">
            <div>
              <p className="ap-stamp">DER FOTOGRAF</p>
              <h2 className="ap-h2 mt-4">Joachim · Frau Vielfalt</h2>
              <p className="ap-lead mt-6 max-w-2xl">Für diese Shootings arbeite ich mit Joachim zusammen. Während ich das Tattoo als Tätowierer und Gestalter kenne, liegt sein Fokus auf Licht, Körper, Perspektive und dem Menschen vor der Kamera.</p>
              <p className="ap-body mt-5 max-w-2xl text-charcoal">So entstehen keine klassischen Dokumentationsfotos, sondern eine fotografische Serie rund um dich und dein Tattoo.</p>
              <a href="https://frauvielfalt.at" target="_blank" rel="noreferrer" className="ap-btn-ghost mt-7">Frau Vielfalt ansehen <ExternalLink size={15} /></a>
            </div>
            <div className="border border-paper-line bg-ink p-8 text-paper">
              <Camera className="text-signal" size={28} />
              <p className="mt-8 ap-stamp text-paper/55">KOOPERATION</p>
              <p className="mt-3 font-display text-3xl font-black uppercase">ARTPART ×<br />FRAU VIELFALT</p>
              <p className="mt-5 text-sm leading-relaxed text-paper/65">Tattoo, Körper und Persönlichkeit werden als zusammenhängendes Bild gedacht.</p>
            </div>
          </div>
        </section>

        <section className="border-b border-paper-line bg-ink py-16 sm:py-20">
          <div className="ap-container flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="ap-stamp text-signal">{invitation.valid ? "DEINE ARTPART-EINLADUNG" : "ARTPART SPECIAL"}</p>
              <div className="mt-4 flex items-baseline gap-4">
                <span className="font-display text-6xl font-black text-paper">{invitation.valid ? "€ 0" : "€ 100"}</span>
                <span className="text-paper/45 line-through">{invitation.valid ? "von ARTPART übernommen" : "regulär € 200"}</span>
              </div>
                <p className="mt-4 max-w-xl text-paper/65">{invitation.valid ? `Persönliche Einladung${invitation.name ? ` für ${invitation.name}` : ""}: ARTPART übernimmt die Kosten für dieses Shooting.` : "Exklusiver Kund:innenpreis im Rahmen der ARTPART Kooperation."}</p>
            </div>
            <div className="grid gap-2 text-sm text-paper/75">
              {["Fotoshooting im Studio", "Natürliche Portrait- und Lifestyle-Aufnahmen", "Tattoo-Detailaufnahmen", "Auswahl und Bearbeitung", "Persönlicher Download-Link"].map((x) => (
                <div key={x} className="flex items-center gap-2"><Check size={15} className="text-signal" />{x}</div>
              ))}
            </div>
          </div>
        </section>

        <section id="anfrage" className="py-16 sm:py-24">
          <div className="ap-container max-w-3xl">
            <p className="ap-stamp">SHOOTING ANFRAGEN</p>
            <h2 className="ap-h2 mt-4">Dein Tattoo vor die Kamera.</h2>
            <p className="ap-body mt-4 text-charcoal">Die Anfrage geht direkt an den Fotografen. Er meldet sich anschließend wegen Termin und Details bei dir.</p>

            {state.ok ? (
              <div className="mt-10 border border-paper-line bg-paper-surface p-8">
                <p className="font-display text-2xl font-black uppercase">Anfrage ist raus.</p>
                <p className="ap-body mt-3 text-charcoal">Joachim hat deine Anfrage erhalten und kann sich direkt bei dir melden.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-10 grid gap-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="grid gap-2"><span className="ap-stamp">Vorname & Name *</span><input className="border border-paper-line bg-paper-surface px-4 py-3" name="name" value={form.name} onChange={change} required /></label>
                  <label className="grid gap-2"><span className="ap-stamp">E-Mail *</span><input className="border border-paper-line bg-paper-surface px-4 py-3" type="email" name="email" value={form.email} onChange={change} required /></label>
                  <label className="grid gap-2"><span className="ap-stamp">Telefon</span><input className="border border-paper-line bg-paper-surface px-4 py-3" name="phone" value={form.phone} onChange={change} /></label>
                  <label className="grid gap-2"><span className="ap-stamp">Körperstelle *</span><input className="border border-paper-line bg-paper-surface px-4 py-3" name="placement" value={form.placement} onChange={change} required /></label>
                </div>
                <label className="grid gap-2"><span className="ap-stamp">Welches Tattoo möchtest du fotografieren lassen? *</span><textarea className="min-h-28 border border-paper-line bg-paper-surface px-4 py-3" name="tattoo" value={form.tattoo} onChange={change} required /></label>
                <label className="grid gap-2"><span className="ap-stamp">Wunschtermin / mögliche Tage</span><input className="border border-paper-line bg-paper-surface px-4 py-3" name="preferred_date" value={form.preferred_date} onChange={change} /></label>
                <label className="grid gap-2"><span className="ap-stamp">Nachricht</span><textarea className="min-h-24 border border-paper-line bg-paper-surface px-4 py-3" name="message" value={form.message} onChange={change} /></label>

                <label className="flex gap-3 border-t border-paper-line pt-5 text-sm leading-relaxed">
                  <input type="checkbox" name="privacy_consent" checked={form.privacy_consent} onChange={change} required className="mt-1 h-4 w-4" />
                  <span>Ich habe die <Link to="/datenschutz" className="underline">Datenschutzerklärung</Link> gelesen und bin damit einverstanden, dass meine Angaben zur Bearbeitung meiner Anfrage verarbeitet und an den ausführenden Fotografen weitergeleitet werden. *</span>
                </label>
                <label className="flex gap-3 text-sm leading-relaxed">
                  <input type="checkbox" name="image_consent" checked={form.image_consent} onChange={change} className="mt-1 h-4 w-4" />
                  <span>Freiwillige Bildfreigabe: Ich bin damit einverstanden, dass ausgewählte Fotos von ARTPART und dem Fotografen für Portfolio-, Website- und Social-Media-Zwecke verwendet werden dürfen. Die Einwilligung kann für die Zukunft widerrufen werden.</span>
                </label>
                <input className="hidden" aria-hidden="true" tabIndex="-1" autoComplete="off" name="honeypot" value={form.honeypot} onChange={change} />
                  <div data-testid="photography-turnstile">
                    <TurnstileWidget onToken={setTurnstileToken} resetKey={turnstileReset} />
                  </div>
                {state.error && <p className="border-l-2 border-signal pl-4 text-sm">{state.error}</p>}
                <button className="ap-btn-solid justify-center sm:justify-self-start" disabled={state.sending}>
                  {state.sending ? "WIRD GESENDET…" : "ANFRAGE ABSENDEN"}
                </button>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer texts={site.texts} />
    </>
  );
}
